import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Minus, Plus } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type PdfDocumentProxy = any;

type WriterPdfViewerProps = {
  data: Uint8Array;
};

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 4;

const WriterPdfPage: React.FC<{
  pdf: PdfDocumentProxy;
  pageNumber: number;
  zoom: number;
}> = ({ pdf, pageNumber, zoom }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const update = () => setWidth(Math.floor(el.clientWidth));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    let cancelled = false;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null = null;

    const render = async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const targetWidth = Math.max(280, Math.min(width - 32, 980)) * zoom;
        const scale = Math.max(0.4, Math.min(5, targetWidth / baseViewport.width));
        const viewport = page.getViewport({ scale });
        const pixelRatio = window.devicePixelRatio || 1;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas rendering is unavailable');

        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.clearRect(0, 0, viewport.width, viewport.height);

        renderTask = page.render({ canvas, canvasContext: context, viewport });
        await renderTask.promise;
        if (!cancelled) setLoading(false);
      } catch (err) {
        if (cancelled || (err as { name?: string })?.name === 'RenderingCancelledException') {
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    };

    void render();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdf, pageNumber, width, zoom]);

  return (
    <div ref={wrapperRef} className="w-full px-4 py-5 flex justify-center">
      <div className="relative max-w-full">
        {loading && (
          <div className="absolute inset-0 min-h-40 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {error ? (
          <div className="min-h-40 w-full max-w-xl border border-border/70 bg-background px-6 py-10 text-center text-sm text-muted-foreground">
            Page {pageNumber} could not be rendered: {error}
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            aria-label={`Compiled PDF page ${pageNumber}`}
            className="block max-w-full border border-border/70 bg-background shadow-sm"
          />
        )}
      </div>
    </div>
  );
};

const clampZoom = (z: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const WriterPdfViewer: React.FC<WriterPdfViewerProps> = ({ data }) => {
  const [pdf, setPdf] = useState<PdfDocumentProxy | null>(null);
  const [zoom, setZoom] = useState(1);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{ distance: number; zoom: number } | null>(null);
  const zoomRef = useRef(1);
  zoomRef.current = zoom;
  const [pageCount, setPageCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [swapping, setSwapping] = useState(false);
  const pdfRef = useRef<PdfDocumentProxy | null>(null);
  const restoreRatioRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loadingTask: any = null;

    const load = async () => {
      // Keep the currently rendered document on screen while the new one
      // parses — clearing it here is what made the pane blank out and jump
      // back to the top on every refresh.
      const hadDoc = !!pdfRef.current;
      if (hadDoc) setSwapping(true);
      else setError(null);
      try {
        loadingTask = pdfjsLib.getDocument({ data: data.slice() });
        const doc = await loadingTask.promise;
        if (cancelled) {
          await doc.destroy?.();
          return;
        }
        const el = scrollerRef.current;
        restoreRatioRef.current =
          hadDoc && el && el.scrollHeight > 0 ? el.scrollTop / el.scrollHeight : null;

        const previous = pdfRef.current;
        pdfRef.current = doc;
        setPdf(doc);
        setPageCount(doc.numPages);
        setError(null);
        // Give the page canvases a beat to re-bind to the new document
        // before tearing the old one down.
        if (previous) setTimeout(() => void previous.destroy?.(), 500);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setSwapping(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      void loadingTask?.destroy();
    };
  }, [data]);

  // Restore the reading position after a document swap. Pages render
  // asynchronously, so re-apply the ratio for a short window while the
  // scroll height settles.
  useEffect(() => {
    const ratio = restoreRatioRef.current;
    if (ratio === null || !pdf) return;
    let frames = 0;
    let raf = 0;
    let aborted = false;
    const el = scrollerRef.current;
    // Any deliberate user input wins over the restore loop.
    const abort = () => {
      aborted = true;
      restoreRatioRef.current = null;
    };
    el?.addEventListener('wheel', abort, { passive: true });
    el?.addEventListener('pointerdown', abort, { passive: true });
    el?.addEventListener('keydown', abort);
    const apply = () => {
      if (aborted) return;
      const node = scrollerRef.current;
      if (node && node.scrollHeight > 0) node.scrollTop = ratio * node.scrollHeight;
      frames += 1;
      if (frames < 30) raf = requestAnimationFrame(apply);
      else restoreRatioRef.current = null;
    };
    raf = requestAnimationFrame(apply);
    return () => {
      cancelAnimationFrame(raf);
      el?.removeEventListener('wheel', abort);
      el?.removeEventListener('pointerdown', abort);
      el?.removeEventListener('keydown', abort);
    };
  }, [pdf, pageCount]);


  // Trackpad pinch (ctrl+wheel) and two-finger touch pinch. Both are
  // handled natively/non-passively so the browser does not page-zoom.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
      setZoom((z) => clampZoom(z * Math.exp(-dy * 0.0015)));
    };

    const distance = () => {
      const [a, b] = [...pointersRef.current.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 2) {
        pinchRef.current = { distance: distance(), zoom: zoomRef.current };
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointersRef.current.size === 2 && pinchRef.current) {
        e.preventDefault();
        const start = pinchRef.current;
        if (start.distance > 0) {
          setZoom(clampZoom((start.zoom * distance()) / start.distance));
        }
      }
    };
    const onPointerUp = (e: PointerEvent) => {
      pointersRef.current.delete(e.pointerId);
      if (pointersRef.current.size < 2) pinchRef.current = null;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove, { passive: false });
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [pdf]);

  if (error) {
    return (
      <div className="flex min-h-80 items-center justify-center px-6 text-center text-sm text-muted-foreground">
        PDF renderer could not open this file: {error}
      </div>
    );
  }

  if (!pdf) {
    return (
      <div className="flex min-h-80 flex-col items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <p className="text-xs uppercase tracking-widest">Preparing PDF renderer…</p>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <div
        ref={scrollerRef}
        className="slim-scrollbar h-full w-full overflow-auto bg-muted/20 py-2"
        aria-label="Compiled PDF viewer"
        style={{ touchAction: 'pan-x pan-y' }}
      >
        {Array.from({ length: pageCount }, (_, index) => (
          <WriterPdfPage key={index + 1} pdf={pdf} pageNumber={index + 1} zoom={zoom} />
        ))}
      </div>
      {swapping && (
        <div
          role="status"
          className="pointer-events-none absolute right-3 top-3 flex items-center gap-1.5 rounded-full border border-border/70 bg-background/90 px-2.5 py-1 text-[11px] text-muted-foreground shadow-sm backdrop-blur"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          Updating…
        </div>
      )}
      <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full border border-border/70 bg-background/90 px-1.5 py-1 shadow-sm backdrop-blur">
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => setZoom((z) => clampZoom(z / 1.25))}
          className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="w-10 text-center text-[11px] tabular-nums text-muted-foreground">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => setZoom((z) => clampZoom(z * 1.25))}
          className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

export default WriterPdfViewer;